import { useQuery } from '@tanstack/react-query';
import { customerApiClient } from '@/services/customer-api-client';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import type { CompanyBranding } from '@/services/company-profile.service';
import { DEFAULT_BRAND_NAME } from './use-tenant-branding';

const EMPTY_BRANDING: CompanyBranding = {
  companyTitle: null,
  logoUrl: null,
  faviconUrl: null,
};

/**
 * Tenant branding for the Customer Portal.
 *
 * Separate from `useTenantBranding` because the portals hold different tokens:
 * the staff endpoint rejects a customer JWT, so this reads
 * `/customer-portal/branding`, which is guarded by CustomerJwtAuthGuard and
 * scoped to the tenant inside that token.
 *
 * Only runs while a customer session exists — the customer login screen has no
 * tenant context and therefore keeps the product default.
 */
export function useCustomerBranding(): CompanyBranding & { brandName: string } {
  const isAuthenticated = useCustomerAuthStore((s) => s.isAuthenticated);

  const { data } = useQuery({
    queryKey: ['customer-portal', 'branding'],
    queryFn: () =>
      customerApiClient
        .get<CompanyBranding>('/customer-portal/branding')
        .then((r) => r.data as CompanyBranding),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const branding = data ?? EMPTY_BRANDING;

  return {
    ...branding,
    brandName: branding.companyTitle?.trim() || DEFAULT_BRAND_NAME,
  };
}
